import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { SupplierCreateRequest } from '@/api/handlers/resources/suppliers/requests';
import type { SupplierWriteResponse } from '@/api/handlers/resources/suppliers/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { SupplierQueryKeys } from './supplierQueryKeys';

/**
 * Mutation behind the Add Supplier submit. The bos-api takes the cropper
 * payload server-side (multipart) so the round-trip is a single POST. The list
 * is server-paginated and server-ordered (by name), so where the new supplier
 * lands is the server's call — we invalidate the cached pages (and the picker)
 * and let them refetch rather than guessing a position with an optimistic
 * insert.
 */
export const useCreateSupplierMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<SupplierWriteResponse>,
        Error,
        SupplierCreateRequest
    >({
        mutationFn: (body) => apiClient.suppliers.createSupplier(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: SupplierQueryKeys.all,
                });
            }
        },
    });
};
